/**
 * Utility functions for the streaming player
 */

import axios from 'axios';

/**
 * Converts a video URL to an HLS (.m3u8) URL if possible
 * Attempts to fetch the .m3u8 version of the video, falls back to original URL if not available
 *
 * @param src - The source URL of the video
 * @returns The HLS URL or the original URL if HLS is not available
 */
const convertToHLSUrl = async (src: string): Promise<string> => {
  if (!src) throw new Error();

  // Check if the URL already ends with .m3u8
  if (src.toLowerCase().endsWith('.m3u8')) {
    return src;
  }

  // Check if the URL ends with common video extensions that might have HLS variants
  const videoExtensions = ['.mp4', '.webm', '.mov'];
  const hasVideoExtension = videoExtensions.some((ext) => src.toLowerCase().endsWith(ext));

  if (hasVideoExtension) {
    // Replace the extension with .m3u8
    const m3u8Url = src.replace(
      src.substring(src.lastIndexOf('.')),
      '.m3u8',
    );

    try {
      // Check if the HLS version exists
      const response = await axios.get(m3u8Url);
      if (response.status === 200) {
        return m3u8Url;
      }
    } catch {
      return src; // If the m3u8 is not available, return the original URL
    }
  } else {
    throw new Error(); // If the URL does not have a video extension, throw an error
  }

  // If HLS is not available or URL doesn't have a video extension, return the original URL
  return src;
};

export default convertToHLSUrl;
export { convertToHLSUrl };
